import { ReactNode } from 'react';
import { ImageLoader } from '../../../ImageLoader';
import { useLocale } from '../../../../i18n/locale';
import { getImageAssetWithLocale, imageKeys } from './imageAssets';
import { ChapterHeadline } from '../chapterComponents/ChapterHeadline';
import { ChapterSubHeadline } from '../chapterComponents/ChapterSubHeadline';
import {
  CustomFormattedMessage,
  TranslationsKeys,
  useCustomIntl,
} from '../../../../i18n/i18n';
import { BookingStatusLabel } from '../../../../pages/bookings/components/BookingStatusLabel';
import { BookingStatus } from '../../../../codegen/bookings';
import { BOOKING_STATUS_TO_TRANSLATION_KEY } from '../../../../i18n/enumMappings';
import { appConfiguration } from '../../../../configuration/appConfiguration';

// Column widths from the "Updated Manual" Figma frame.
const TEXT_MAX_WIDTH = 409;
const IMAGE_WIDTH = 470;
const STATUS_LABEL_WIDTH = 110;

const ManualSection = ({
  children,
  image,
  imageAlt,
}: {
  children: ReactNode;
  image: string;
  imageAlt: string;
}) => (
  <div className="display-flex flex-column flex-row-md gap-50 margin-bottom-25">
    <div
      className="flex-1-1"
      style={{ maxWidth: TEXT_MAX_WIDTH }}
    >
      {children}
    </div>
    <div style={{ width: IMAGE_WIDTH, maxWidth: '100%', flexShrink: 0 }}>
      <ImageLoader
        src={image}
        alt={imageAlt}
        className="border"
        enlargeable
      />
    </div>
  </div>
);

const statuses: { status: BookingStatus; description: TranslationsKeys }[] = [
  {
    status: BookingStatus.Planned,
    description: 'userManual.chapter.bookings.bookingOverview.statuses.planned',
  },
  {
    status: BookingStatus.InProgress,
    description:
      'userManual.chapter.bookings.bookingOverview.statuses.inProgress',
  },
  {
    status: BookingStatus.Completed,
    description:
      'userManual.chapter.bookings.bookingOverview.statuses.completed',
  },
  {
    status: BookingStatus.Canceled,
    description:
      'userManual.chapter.bookings.bookingOverview.statuses.canceled',
  },
];

export const BookingsBookingOverviewChapter = () => {
  const { locale } = useLocale();
  const { formatMessage } = useCustomIntl();

  const statusName = (status: BookingStatus) =>
    formatMessage({ id: BOOKING_STATUS_TO_TRANSLATION_KEY[status] });

  const headline = formatMessage({ id: 'userManual.bookingOverview.headline' });

  return (
    <div>
      <div className="row">
        <ChapterHeadline messageId="userManual.bookingOverview.headline" />
      </div>
      <ManualSection
        image={getImageAssetWithLocale(
          imageKeys.bookingsBookingOverview1,
          locale,
        )}
        imageAlt={headline}
      >
        <CustomFormattedMessage
          id="userManual.chapter.bookings.bookingOverview.intro"
          values={{
            bookingsTab: formatMessage({
              id: 'base.header.navigationLinks.bookings',
            }),
            fleetMonitor: (content: ReactNode) => (
              <a
                href={appConfiguration.fleetMonitorWidgetLink}
                target="_blank"
                rel="noreferrer"
                className="text-color-primary text-medium text-decoration-underline"
              >
                {content}
              </a>
            ),
          }}
        />
      </ManualSection>
      <ManualSection
        image={getImageAssetWithLocale(
          imageKeys.bookingsBookingOverview2,
          locale,
        )}
        imageAlt={headline}
      >
        <CustomFormattedMessage
          id="userManual.chapter.bookings.bookingOverview.details"
          values={{
            cancelButton: formatMessage({ id: 'common.booking.button.cancel' }),
          }}
        />
      </ManualSection>
      <div className="row margin-top-25">
        <ChapterSubHeadline messageId="userManual.bookingOverview.status.subHeadline" />
      </div>
      <p className="margin-top-0 margin-bottom-25">
        <CustomFormattedMessage
          id="userManual.chapter.bookings.bookingOverview.statuses.text"
          values={{
            planned: statusName(BookingStatus.Planned),
            inProgress: statusName(BookingStatus.InProgress),
            completed: statusName(BookingStatus.Completed),
            canceled: statusName(BookingStatus.Canceled),
          }}
        />
      </p>
      <div className="display-flex flex-column gap-10">
        {statuses.map(({ status, description }) => (
          <div
            key={status}
            className="display-flex align-items-center"
          >
            <div
              className="flex-0-0"
              style={{ width: STATUS_LABEL_WIDTH, flexShrink: 0 }}
            >
              <BookingStatusLabel status={status} />
            </div>
            <div>
              <CustomFormattedMessage
                id={description}
                values={{ status: statusName(status) }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
